import { h, Component } from 'preact';
import './index.less';
import SearchIcon from '../../molecules/Icon/Search';
import MultiplyIcon from '../../molecules/Icon/Multiply';
import BarcodeIcon from '../../molecules/Icon/Barcode';
import { getCatalogueTheme } from '../../lib/Theme';
import {
  getSearchTerm,
  setSearchTerm,
  addSearchListener,
  addSearchStateListener,
  removeSearchStateListener,
  removeSearchListener,
  getSearchState,
  deactivateSearch,
  cancelSearchDebounce,
  setBarcodeModeEnabled,
  getBarcodeModeEnabled
} from '../../lib/Search';
import { showCreateFreeCatalogueBanner } from '../../lib/Amalgam';
import { NAVBAR_BANNER_HEIGHT } from '../../../config';
import I18n from '../../lib/I18n';
import EventBus from '../../lib/EventBus';
import { getPrimaryLoaderStyle, isLightColor } from '../../lib/Utils';
import { setNativeStatusBarColor } from '../../../../../nativeConnectors/platform';
import { getCurrentRouteName, NAME as ROUTE_NAME } from '../../lib/Route';

class NavBarSearchBar extends Component {
  constructor (props) {
    super(props);

    this.state = {
      searchState: getSearchState(),
      searchTerm: getSearchTerm(),
      searchInProgress: false,
      barcodeModeEnabled: getBarcodeModeEnabled()
    };
  }

  componentDidMount () {
    addSearchStateListener(this.onSearchStateChange);
    addSearchListener(this.onSearchChange);
    EventBus.addListener(EventBus.TYPE.SEARCH_STARTED, this.onSearchStarted);
    EventBus.addListener(EventBus.TYPE.SEARCH_DONE, this.onSearchDone);
    EventBus.addListener(EventBus.TYPE.FOCUS_NAVBAR_SEARCH, this.focusSearchInput);
    window.addEventListener('popstate', this.onBackButtonPressClearSearch);
    if (this.state.searchState && this.input) {
      this.input.focus();
    }
  }

  componentDidUpdate (previousProps, previousState) {
    if (!previousState.searchState && this.state.searchState && this.input) {
      setTimeout(() => {
        this.input.focus();
      });
    }
  }

  componentWillUnmount () {
    cancelSearchDebounce();
    removeSearchStateListener(this.onSearchStateChange);
    removeSearchListener(this.onSearchChange);
    EventBus.removeListener(EventBus.TYPE.SEARCH_STARTED, this.onSearchStarted);
    EventBus.removeListener(EventBus.TYPE.SEARCH_DONE, this.onSearchDone);
    EventBus.removeListener(EventBus.TYPE.FOCUS_NAVBAR_SEARCH, this.focusSearchInput);
    window.removeEventListener('popstate', this.onBackButtonPressClearSearch);
  }

  onBackButtonPressClearSearch = () => {
    deactivateSearch(false);
    this.onSearchStateChange(null, false);
  };

  onSearchStateChange = (searchState, notify = true) => {
    this.setState({ searchState: getSearchState() }, () => {
      if (!notify) {
        return;
      }
      if (this.state.searchState) {
        history.pushState(null, '', window.location);
      } else {
        history.go(-1);
      }
    });
  };

  onSearchChange = () => {
    this.setState({ searchTerm: getSearchTerm() });
  };

  onSearchStarted = () => {
    this.setState({ searchInProgress: true });
  };

  onSearchDone = () => {
    this.setState({ searchInProgress: false }, () => {
      if (this.state.barcodeModeEnabled && this.input && getSearchTerm() !== '') {
        // Clear input when barcode mode is enabled
        setSearchTerm('');
        this.input.value = '';
      }

      setTimeout(() => {
        if (this.input && this.state.searchState) {
          this.input.focus();
        }
      }, 100);
    });
  };

  onBarcodeToggle = () => {
    const newBarcodeModeEnabled = !this.state.barcodeModeEnabled;
    this.setState({ barcodeModeEnabled: newBarcodeModeEnabled });
    setBarcodeModeEnabled(newBarcodeModeEnabled);
  };

  onInputChange = (e) => {
    setSearchTerm(e.target.value);
  };

  onCloseClick = () => {
    deactivateSearch();
  };

  onClearClick = () => {
    setSearchTerm('');
  };

  onSearchSubmit = (e) => {
    e.preventDefault();

    if (this.input) {
      this.input.blur();
    }
  };

  onInputFocus = () => {
    setNativeStatusBarColor('#ffffff');
  };

  onInputBlur = () => {
    const { homeGrid } = getCatalogueTheme() || {};
    const { backgroundColor } = homeGrid || {};
    setNativeStatusBarColor(backgroundColor);
  };

  focusSearchInput = () => {
    if (this.state.barcodeModeEnabled && this.input) {
      setTimeout(() => {
        this.input.focus();
      }, 50);
    }
  };

  render () {
    const { searchState, searchTerm, searchInProgress, barcodeModeEnabled } = this.state;

    const homepageTheme = getCatalogueTheme().homepage || {};
    const homegridTheme = getCatalogueTheme().homeGrid || {};

    if (!searchState) {
      return null;
    }

    const currentRoute = getCurrentRouteName();

    return (
      <div
        className={`nav-bar-search-bar ${homegridTheme.searchClass}`}
        style={{
          backgroundColor:
            currentRoute === ROUTE_NAME.GALLERY ||
            currentRoute === ROUTE_NAME.RESELLER_CATALOGUE_GALLERY
              ? homepageTheme.collapsedHeaderBackground
              : homegridTheme.backgroundColor,
          top: showCreateFreeCatalogueBanner() ? NAVBAR_BANNER_HEIGHT : 0
        }}
      >
        <div className="bar">
          <div className="close-icon" onClick={this.onCloseClick}>
            <MultiplyIcon
              color={
                homegridTheme.searchClass === 'dark'
                  ? '#fff'
                  : homegridTheme.searchIconColor || '#3833EA'
              }
              size={12}
            />
          </div>

          <form action="." className="search-input-container" onSubmit={this.onSearchSubmit}>
            <input
              ref={(element) => (this.input = element)}
              type="search"
              style={{
                color: isLightColor(homegridTheme.backgroundColor) ? '#000' : '#FFF'
              }}
              placeholder={I18n.t('SEARCH_FOR_PRODUCTS')}
              value={searchTerm}
              onInput={this.onInputChange}
              onFocus={this.onInputFocus}
              onBlur={this.onInputBlur}
            />
            <div
              className={`barcode-toggle ${barcodeModeEnabled ? 'active' : ''}`}
              onClick={this.onBarcodeToggle}
              style={{
                backgroundColor: barcodeModeEnabled
                  ? homegridTheme.searchIconColor || '#3833EA'
                  : '#fff',
                border: barcodeModeEnabled
                  ? 'none'
                  : `1px solid ${homegridTheme.searchClass === 'dark' ? '#ccc' : '#ddd'}`
              }}
            >
              <BarcodeIcon
                color={barcodeModeEnabled ? '#fff' : homegridTheme.searchIconColor || '#3833EA'}
                size={16}
              />
            </div>
          </form>

          <div className="search-icon" onClick={this.onSearchSubmit}>
            {searchInProgress ? (
              <div className="search-loader">
                <div className="loader" style={getPrimaryLoaderStyle()} />
              </div>
            ) : (
              <SearchIcon
                color={
                  homegridTheme.searchClass === 'dark' ? '#fff' : homegridTheme.searchIconColor
                }
                size={18}
              />
            )}
          </div>
        </div>
      </div>
    );
  }
}

export default NavBarSearchBar;
